import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import FileUpload from "@/components/FileUpload";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { inputCls, Field } from "./ui";
import { toast } from "sonner";

const blank = { title: "", description: "", cover_path: null, price: 0, currency: "eur", download_path: null, status: "draft" };

export default function ProductManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => { api.get("/admin/products").then((r) => setItems(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    const payload = { ...editing, price: parseFloat(editing.price) || 0 };
    try {
      if (editing.id) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Salvato"); setEditing(null); load();
    } catch { toast.error("Errore"); } finally { setSaving(false); }
  };

  const remove = async (id) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/admin/products/${id}`); load(); };

  return (
    <div data-testid="cms-products">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl">Shop · Prodotti</h2>
        <button onClick={() => setEditing({ ...blank })} data-testid="cms-new-product"
          className="inline-flex items-center gap-2 bg-[#C6899E] text-[#4A2233] rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.12em] hover:bg-[#B4718C]">
          <Plus className="w-4 h-4" /> Nuovo
        </button>
      </div>

      {items.length === 0 ? <p className="text-[#9C8F94] py-10 text-center">Nessun prodotto.</p> : (
        <div className="space-y-3">
          {items.map((p) => (
            <div key={p.id} className="flex items-center gap-4 bg-[#FBF5F1] border border-[rgba(61,50,56,0.08)] rounded-xl p-3">
              <div className="w-14 h-14 rounded-lg overflow-hidden bg-[#F3E8E2] shrink-0">{p.cover_path ? <img src={mediaUrl(p.cover_path)} alt="" className="w-full h-full object-cover" /> : null}</div>
              <div className="flex-1 min-w-0"><div className="text-[#3D3238] truncate">{p.title}</div><div className="text-xs text-[#C6899E] mt-1">€{p.price} · {p.status === "published" ? "Pubblicato" : "Bozza"}</div></div>
              <button onClick={() => setEditing({ ...p })} className="p-2 text-[#8C7E83] hover:text-[#C6899E]"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(p.id)} className="p-2 text-[#8C7E83] hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#FFFFFF] border-[rgba(198,137,158,0.2)] max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-[#3D3238]">{editing?.id ? "Modifica" : "Nuovo"} prodotto</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 pt-2">
              <Field label="Titolo"><input className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="cms-product-title" /></Field>
              <Field label="Descrizione"><textarea rows={4} className={inputCls} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></Field>
              <FileUpload label="Copertina" value={editing.cover_path} onChange={(p) => setEditing({ ...editing, cover_path: p })} testid="cms-product-cover" />
              <FileUpload label="File scaricabile (PDF, ebook, zip...)" accept="*" value={editing.download_path} onChange={(p) => setEditing({ ...editing, download_path: p })} testid="cms-product-file" />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Prezzo (€)"><input type="number" step="0.01" className={inputCls} value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="cms-product-price" /></Field>
                <Field label="Stato">
                  <select className={inputCls} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                    <option value="draft">Bozza</option><option value="published">Pubblicato</option>
                  </select>
                </Field>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-[rgba(61,50,56,0.08)]">
                <button onClick={() => setEditing(null)} className="text-sm text-[#9C8F94] px-4 py-2">Annulla</button>
                <button onClick={save} disabled={saving || !editing.title} data-testid="cms-save-product"
                  className="inline-flex items-center gap-2 bg-[#C6899E] text-[#4A2233] rounded-full px-6 py-2.5 text-sm uppercase tracking-[0.12em] hover:bg-[#B4718C] disabled:opacity-40">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Salva
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
